import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  Droplet, ChevronRight, Loader2, CheckCircle, AlertTriangle, 
  Info, TrendingUp, TrendingDown, Minus
} from "lucide-react";
import { toast } from "sonner";

const GlucoseCheckIn = ({ onComplete, initialExpanded = false }) => {
  const { t } = useLanguage();
  const { user, token } = useAuth();
  const [expanded, setExpanded] = useState(initialExpanded);
  const [loading, setLoading] = useState(false);
  const [glucoseValue, setGlucoseValue] = useState("");
  const [readingType, setReadingType] = useState("random");
  const [notes, setNotes] = useState("");
  const [result, setResult] = useState(null);
  
  const userUnit = user?.glucose_unit || "mg/dL";

  const READING_TYPES = [
    { value: "fasting", label: t('glucose.fasting'), description: t('glucose.morning_before_eating') },
    { value: "before_meal", label: t('glucose.before_meal'), description: t('glucose.right_before_eating') },
    { value: "after_meal", label: t('glucose.after_meal'), description: t('glucose.two_hours_after') },
    { value: "bedtime", label: t('glucose.bedtime'), description: t('glucose.before_sleep') },
    { value: "random", label: t('glucose.random'), description: t('glucose.any_other_time') }
  ];

  const handleSubmit = async () => {
    if (!glucoseValue || isNaN(parseFloat(glucoseValue))) {
      toast.error("Please enter a valid glucose value");
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${API}/glucose/check-in`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify({
          glucose_value: parseFloat(glucoseValue),
          reading_type: readingType,
          input_unit: userUnit,
          notes: notes || null
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
        toast.success(t('glucose.glucose_logged'));
        
        // Call parent callback if provided
        if (onComplete) {
          onComplete(data);
        }
      } else {
        toast.error(t('common.error'));
      }
    } catch (error) {
      console.error("Check-in error:", error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setGlucoseValue("");
    setReadingType("random");
    setNotes("");
    setResult(null);
    setExpanded(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "low":
      case "slightly_low":
        return <TrendingDown className="w-5 h-5" />;
      case "normal":
        return <CheckCircle className="w-5 h-5" />;
      case "slightly_elevated":
      case "elevated":
        return <TrendingUp className="w-5 h-5" />;
      case "high":
        return <AlertTriangle className="w-5 h-5" />;
      default:
        return <Minus className="w-5 h-5" />;
    }
  };

  const getStatusColor = (color) => {
    switch (color) {
      case "success":
        return "bg-success/10 text-success border-success/20";
      case "warning":
        return "bg-warning/10 text-warning border-warning/20";
      case "destructive":
        return "bg-destructive/10 text-destructive border-destructive/20";
      default:
        return "bg-muted/10 text-muted-foreground border-muted/20";
    }
  };

  // Result view
  if (result) {
    const { feedback, weekly_average, weekly_average_unit } = result;
    
    return (
      <Card className="bg-white rounded-3xl border border-orange-100/50 overflow-hidden">
        {/* Result Header */}
        <div className={`p-4 ${getStatusColor(feedback?.color)} border-b`}>
          <div className="flex items-center gap-3">
            {getStatusIcon(feedback?.status)}
            <div>
              <p className="font-bold text-lg">
                {result.glucose_value} {result.unit}
              </p>
              <p className="font-medium">{feedback?.status_label}</p>
            </div>
          </div>
        </div>
        
        {/* Feedback */}
        <div className="p-4 space-y-4">
          {/* Tip */}
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">{feedback?.tip}</p>
          </div>
          
          {/* Weekly Average */}
          {weekly_average && (
            <div className="bg-orange-50/50 rounded-xl p-3">
              <p className="text-sm text-muted-foreground">
                {t('glucose.your_7_day_average')}: <span className="font-bold text-secondary">
                  {weekly_average} {weekly_average_unit}
                </span>
              </p>
            </div>
          )}
          
          {/* Reading Type */}
          <p className="text-xs text-muted-foreground text-center">
            {READING_TYPES.find(r => r.value === result.reading_type)?.label} {t('glucose.reading')}
          </p>
          
          {/* Done Button */}
          <Button
            onClick={resetForm}
            variant="outline"
            className="w-full rounded-full"
          >
            {t('common.done')}
          </Button>
        </div>
      </Card>
    );
  }

  // Collapsed view
  if (!expanded) {
    return (
      <Card
        className="bg-gradient-to-r from-primary/5 to-orange-50 rounded-3xl border border-orange-100/50 p-4 cursor-pointer hover:shadow-soft-lift transition-all"
        onClick={() => setExpanded(true)}
        data-testid="glucose-checkin-card"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Droplet className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="font-semibold text-secondary">{t('dashboard.todays_checkin')}</p>
              <p className="text-sm text-muted-foreground">{t('dashboard.how_is_your_glucose')}</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </div>
      </Card>
    );
  }

  // Expanded form view
  return (
    <Card className="bg-white rounded-3xl border border-orange-100/50 overflow-hidden">
      {/* Header */}
      <div className="bg-primary/5 p-4 border-b border-orange-100">
        <div className="flex items-center gap-3">
          <Droplet className="w-6 h-6 text-primary" />
          <div>
            <p className="font-semibold text-secondary">{t('glucose.log_glucose')}</p>
            <p className="text-sm text-muted-foreground">{t('glucose.quick_daily_checkin')}</p>
          </div>
        </div>
      </div>
      
      {/* Form */}
      <div className="p-4 space-y-4">
        {/* Glucose Value */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-secondary">
            {t('glucose.glucose_value')}
          </label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={glucoseValue}
              onChange={(e) => setGlucoseValue(e.target.value)}
              placeholder={userUnit === "mmol/L" ? "e.g., 6.5" : "e.g., 120"}
              className="rounded-xl border-orange-100 text-lg font-medium"
              data-testid="glucose-value-input"
            />
            <span className="text-muted-foreground font-medium min-w-[60px]">
              {userUnit}
            </span>
          </div>
        </div>
        
        {/* Reading Type */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-secondary">
            {t('glucose.reading_type')}
          </label>
          <Select value={readingType} onValueChange={setReadingType}>
            <SelectTrigger className="rounded-xl border-orange-100">
              <SelectValue placeholder={t('glucose.when_reading')} />
            </SelectTrigger>
            <SelectContent>
              {READING_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  <div>
                    <p>{type.label}</p>
                    <p className="text-xs text-muted-foreground">{type.description}</p>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Notes (optional) */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-secondary">
            {t('glucose.notes')} <span className="text-muted-foreground font-normal">({t('common.optional')})</span>
          </label>
          <Input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('glucose.any_additional_notes')}
            className="rounded-xl border-orange-100"
          />
        </div>
        
        {/* Buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            variant="outline"
            onClick={() => setExpanded(false)}
            className="flex-1 rounded-full"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !glucoseValue}
            className="flex-1 rounded-full bg-primary text-white hover:bg-primary/90"
            data-testid="submit-glucose-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('common.processing')}
              </>
            ) : (
              t('glucose.log_glucose')
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default GlucoseCheckIn;
